import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";

// ═══════════════════════════════════════════════════════════
// STORAGE — single localStorage key per user
// ═══════════════════════════════════════════════════════════
const STORE_KEY = "imago_tour_";
function _uid() { return sessionStorage.getItem("userId"); }
function _read() {
  const u = _uid(); if (!u) return {};
  try { return JSON.parse(localStorage.getItem(STORE_KEY + u)) || {}; } catch { return {}; }
}
function _write(d) { const u = _uid(); if (u) localStorage.setItem(STORE_KEY + u, JSON.stringify(d)); }
function markDone(id) { const s = _read(); s[id] = 1; _write(s); }
function isDone(id) { return !!_read()[id]; }

function isMobile() { return window.innerWidth < 768; }

// Check if an element is actually visible on screen
function isVisible(selector) {
  const el = document.querySelector(selector);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 &&
    rect.bottom > 0 && rect.top < window.innerHeight &&
    rect.right > 0 && rect.left < window.innerWidth;
}

// Only keep steps whose targets are actually visible on screen
function filterVisible(steps) {
  return steps.filter(s => isVisible(s.target));
}

// ═══════════════════════════════════════════════════════════
// STEP DEFINITIONS — mobile-aware, visibility-filtered
// ═══════════════════════════════════════════════════════════
const STEP_DEFS = {
  sidebar: () => {
    const mobile = isMobile();
    if (mobile) {
      return filterVisible([
        { target: '[data-walkthrough="logo"]', title: "👋 Welcome to Imago", content: "Your API testing & automation platform. Let's give you a quick tour of the key features.", placement: "bottom", disableBeacon: true },
      ]);
    }
    return filterVisible([
      { target: '[data-walkthrough="logo"]', title: "👋 Welcome to Imago", content: "Your API testing & automation platform. Let's give you a quick tour of everything.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="sidebar-projects"]', title: "📁 Projects", content: "Build and test your APIs here — this is where most of the action happens.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="sidebar-admin"]', title: "⚙️ Admin", content: "Manage your organization settings, users, and project permissions.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="sidebar-environment"]', title: "🌍 Environments", content: "Manage variables like base URLs, tokens, and keys for different environments.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="sidebar-test-plans"]', title: "📋 Test Plans", content: "Monitor responses from your scheduled API executions in real time.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="sidebar-test-logs"]', title: "📊 Test Logs", content: "View detailed run history for all tests executed manually.", placement: "right", disableBeacon: true },
    ]);
  },

  api: () => {
    const mobile = isMobile();
    return filterVisible([
      { target: '[data-walkthrough="tab-switcher"]', title: "🔀 API & Orchestration", content: "Switch between single API testing and multi-step orchestration workflows.", placement: "bottom", disableBeacon: true },
      ...(mobile
        ? [{ target: '[data-walkthrough="project-tree-mobile"]', title: "📁 Project Explorer", content: "Tap here to open your projects and collections.", placement: "bottom", disableBeacon: true }]
        : [{ target: '[data-walkthrough="project-tree"]', title: "📁 Project Explorer", content: "All your projects, collections, and API requests live here.", placement: "right", disableBeacon: true }]
      ),
      { target: '[data-walkthrough="env-selector"]', title: "🌍 Environments", content: "Select an environment to inject variables like base URLs, tokens, and keys.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="save-btn"]', title: "💾 Save Your Work", content: "Don't forget to save — headers, body, scripts, and auth are all persisted.", placement: "bottom", disableBeacon: true },
    ]);
  },

  orch: () => {
    const mobile = isMobile();
    if (mobile) {
      // Mobile: try collapsible sections first, fall back to tab-switcher hint
      const mobileSteps = filterVisible([
        { target: '[data-walkthrough="orch-list-mobile"]', title: "📋 Orchestrations", content: "All your orchestration workflows are listed here. Tap to expand and select one.", placement: "bottom", disableBeacon: true },
        { target: '[data-walkthrough="orch-steps-mobile"]', title: "🔗 Steps", content: "View and manage the API steps in your orchestration. Tap to expand.", placement: "bottom", disableBeacon: true },
      ]);
      // If collapsible sections aren't visible (no orchestration selected),
      // show a fallback step pointing at the tab-switcher area
      if (mobileSteps.length === 0) {
        return filterVisible([
          { target: '[data-walkthrough="tab-switcher"]', title: "📋 Orchestration Tab", content: "You're on the Orchestration tab. Select an orchestration from the list to manage multi-step API workflows.", placement: "bottom", disableBeacon: true },
        ]);
      }
      // Also try to show execute-all and actions if visible
      return [
        ...mobileSteps,
        ...filterVisible([
          { target: '[data-walkthrough="orch-execute-all"]', title: "▶️ Execute All", content: "Run the entire orchestration end-to-end with one tap.", placement: "bottom", disableBeacon: true },
          { target: '[data-walkthrough="orch-actions"]', title: "➕ Add Step & Schedule", content: "Add new API steps or schedule the orchestration to run automatically.", placement: "bottom", disableBeacon: true },
        ]),
      ];
    }
    // Desktop: standard side-by-side layout
    return filterVisible([
      { target: '[data-walkthrough="orch-list"]', title: "📋 Orchestrations", content: "All your orchestration workflows are listed here. Select one to view its steps.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="orch-steps"]', title: "🔗 Step Pipeline", content: "Each step is an API call executed in order. Select a step to see its details.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="orch-execute-all"]', title: "▶️ Execute All", content: "Run the entire orchestration end-to-end with one click.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="orch-actions"]', title: "➕ Add Step & Schedule", content: "Add new API steps or schedule the orchestration to run automatically.", placement: "bottom", disableBeacon: true },
    ]);
  },

  env: () => {
    const mobile = isMobile();
    if (mobile) {
      // On mobile the sidebar is off-screen (fixed + translate-x-full).
      // Only show the variables panel which is always visible.
      return filterVisible([
        { target: '[data-walkthrough="env-variables-panel"]', title: "📝 Environments", content: "Manage your environment variables here — base URLs, tokens, and keys get injected into API requests at runtime.", placement: "bottom", disableBeacon: true },
      ]);
    }
    return filterVisible([
      { target: '[data-walkthrough="env-sidebar"]', title: "📂 Environment List", content: "All your environments are listed here. Click one to view and edit its variables.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="env-actions"]', title: "➕ Create & Import", content: "Create a new environment or import one from a JSON file.", placement: "right", disableBeacon: true },
      { target: '[data-walkthrough="env-variables-panel"]', title: "📝 Variables Panel", content: "Add key-value pairs here — these get injected into your API requests at runtime.", placement: "left", disableBeacon: true },
    ]);
  },

  testplans: () => {
    const mobile = isMobile();
    if (mobile) {
      // On mobile the table columns with filters are off-screen (min-w-[900px] table).
      // Show only the tabs and search which are always visible.
      return filterVisible([
        { target: '[data-walkthrough="tp-tabs"]', title: "📋 Test Plans", content: "Switch between API and Orchestration scheduled tests.", placement: "bottom", disableBeacon: true },
        { target: '[data-walkthrough="tp-search"]', title: "🔍 Search & Filters", content: "Search for tests by name. Scroll the table to access status, environment, and type filters.", placement: "bottom", disableBeacon: true },
      ]);
    }
    return filterVisible([
      { target: '[data-walkthrough="tp-tabs"]', title: "📋 Test Plans", content: "Switch between API and Orchestration scheduled tests.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="tp-search"]', title: "🔍 Search Tests", content: "Quickly find any scheduled test by name.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="tp-status-filter"]', title: "📊 Filter by Status", content: "Click the dropdown to filter by Active or Inactive.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="tp-env-filter"]', title: "🌍 Filter by Environment", content: "Filter tests by their target environment.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="tp-type-filter"]', title: "🏷️ Filter by Type", content: "Filter between Single API and Load Test types.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="tp-actions"]', title: "⚡ Actions", content: "View results or delete a scheduled test.", placement: "bottom", disableBeacon: true },
    ]);
  },

  logs: () => {
    const mobile = isMobile();
    if (mobile) {
      // On mobile, detail panel may not be visible. Show what's on screen.
      return filterVisible([
        { target: '[data-walkthrough="logs-type-tabs"]', title: "📋 Log Filters", content: "Filter by type — All Logs, Single API, Orchestration, or Load Test.", placement: "bottom", disableBeacon: true },
        { target: '[data-walkthrough="logs-date-picker"]', title: "📅 Date Picker", content: "Select a date to view execution logs from that day.", placement: "bottom", disableBeacon: true },
        { target: '[data-walkthrough="logs-exec-list"]', title: "📜 Executions", content: "Browse all test executions. Tap any entry to view its details.", placement: "bottom", disableBeacon: true },
      ]);
    }
    return filterVisible([
      { target: '[data-walkthrough="logs-type-tabs"]', title: "📋 Log Type Filters", content: "Filter by type — All Logs, Single API, Orchestration, or Load Test.", placement: "bottom-start", disableBeacon: true },
      { target: '[data-walkthrough="logs-date-picker"]', title: "📅 Date Picker", content: "Select a date to view execution logs from that day.", placement: "bottom", disableBeacon: true },
      { target: '[data-walkthrough="logs-exec-list"]', title: "📜 Executions List", content: "Browse all test executions. Click any entry to view details.", placement: "right-start", disableBeacon: true },
      { target: '[data-walkthrough="logs-detail-panel"]', title: "🔍 Execution Details", content: "View request/response data, orchestration flow, or load test metrics.", placement: "left-start", disableBeacon: true },
    ]);
  },
};

// ═══════════════════════════════════════════════════════════
// PHASE CONFIG
// ═══════════════════════════════════════════════════════════
const PHASES = [
  { id: "sidebar",   path: "/inputs",               switchTab: null },
  { id: "api",       path: "/inputs",               switchTab: "API" },
  { id: "orch",      path: "/inputs",               switchTab: "Orchestration" },
  { id: "env",       path: "/environment",           switchTab: null },
  { id: "testplans", path: "/scheduled-test-plans",  switchTab: null },
  { id: "logs",      path: "/test-logs",             switchTab: null },
];

function markAllDone() {
  const s = _read();
  PHASES.forEach(p => { s[p.id] = 1; });
  s.global = 1;
  _write(s);
}

// ═══════════════════════════════════════════════════════════
// CUSTOM TOOLTIP
// ═══════════════════════════════════════════════════════════
function CustomTooltip({ index, step, backProps, primaryProps, skipProps, size, isLastPhase }) {
  const progress = ((index + 1) / size) * 100;
  return (
    <div style={{
      width: "min(340px, 90vw)", background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
      borderRadius: 16, padding: 0, color: "#fff",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(99,102,241,0.15)",
      border: "1px solid rgba(99,102,241,0.2)", overflow: "hidden",
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      <div style={{ height: 4, background: "rgba(255,255,255,0.08)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg,#6366f1,#a855f7,#ec4899)", borderRadius: "0 2px 2px 0", transition: "width 0.4s cubic-bezier(.4,0,.2,1)" }} />
      </div>
      <div style={{ padding: "20px 22px 18px" }}>
        {step.title && <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{step.title}</div>}
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,0.78)", marginBottom: 18 }}>{step.content}</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16 }}>
          {Array.from({ length: size }).map((_, i) => (
            <div key={i} style={{
              width: i === index ? 20 : 7, height: 7, borderRadius: 4,
              background: i === index ? "linear-gradient(90deg,#6366f1,#a855f7)" : "rgba(255,255,255,0.18)",
              transition: "all 0.35s cubic-bezier(.4,0,.2,1)",
            }} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button {...skipProps} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: 12.5, cursor: "pointer", padding: "4px 2px", fontFamily: "inherit" }}>Skip Tour</button>
          <div style={{ display: "flex", gap: 8 }}>
            {index > 0 && (
              <button {...backProps} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 13, fontWeight: 600, padding: "7px 18px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit" }}>Back</button>
            )}
            <button {...primaryProps} style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, padding: "7px 22px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 15px rgba(99,102,241,0.35)" }}>
              {index === size - 1 ? (isLastPhase ? "Finish" : "Continue →") : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

CustomTooltip.propTypes = {
  index: PropTypes.number.isRequired,
  step: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }).isRequired,
  backProps: PropTypes.object.isRequired,
  primaryProps: PropTypes.object.isRequired,
  skipProps: PropTypes.object.isRequired,
  size: PropTypes.number.isRequired,
  isLastPhase: PropTypes.bool,
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function Walkthrough() {
  const navigate = useNavigate();
  const location = useLocation();

  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);

  const phaseRef = useRef(0);
  const runningRef = useRef(false);
  const pollTimerRef = useRef(null);
  const pendingNavRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
  }, []);

  // Click the correct tab button inside the workspace tab-switcher
  const clickTab = useCallback((label) => {
    if (!label) return;
    const container = document.querySelector('[data-walkthrough="tab-switcher"]');
    if (!container) return;
    for (const btn of container.querySelectorAll("button")) {
      if (btn.textContent.trim().toLowerCase().includes(label.toLowerCase())) {
        btn.click();
        return;
      }
    }
  }, []);

  // Start a specific phase
  const startPhase = useCallback((idx, currentPath) => {
    clearPoll();

    // Skip already-done phases
    while (idx < PHASES.length && isDone(PHASES[idx].id)) idx++;
    if (idx >= PHASES.length) { markAllDone(); setRun(false); runningRef.current = false; return; }

    const phase = PHASES[idx];
    phaseRef.current = idx;

    // Navigate if needed
    if (currentPath !== phase.path) {
      setRun(false);
      runningRef.current = false;
      pendingNavRef.current = phase.path;
      navigate(phase.path);
      return;
    }

    // Switch tab if needed
    if (phase.switchTab) clickTab(phase.switchTab);

    // Poll until we get at least 1 visible step
    // Give extra time for orchestration on mobile (needs auto-select + render)
    let attempt = 0;
    const isOrchMobile = phase.id === "orch" && isMobile();
    const maxAttempts = isOrchMobile ? 60 : phase.switchTab ? 50 : 40;
    const initialDelay = isOrchMobile ? 1200 : phase.switchTab ? 900 : 600;

    const poll = () => {
      const built = STEP_DEFS[phase.id]();
      if (built.length > 0) {
        setSteps(built);
        setStepIndex(0);
        runningRef.current = true;
        requestAnimationFrame(() => {
          setTimeout(() => setRun(true), 80);
        });
      } else if (attempt < maxAttempts) {
        attempt++;
        pollTimerRef.current = setTimeout(poll, 350);
      } else {
        // No visible targets after all attempts — skip this phase
        markDone(phase.id);
        startPhase(idx + 1, currentPath);
      }
    };
    pollTimerRef.current = setTimeout(poll, initialDelay);
  }, [clearPoll, clickTab, navigate]);

  // On location change: resume or start tour
  useEffect(() => {
    const path = location.pathname;

    // Resuming after navigation?
    if (pendingNavRef.current && path === pendingNavRef.current) {
      pendingNavRef.current = null;
      startPhase(phaseRef.current, path);
      return () => clearPoll();
    }

    // Already running on this page? Don't restart.
    if (runningRef.current) return;

    // Global tour done? Check for unseen page-specific tour
    if (isDone("global")) {
      const pagePhase = PHASES.find(p => p.path === path);
      if (!pagePhase || isDone(pagePhase.id)) return;
      startPhase(PHASES.indexOf(pagePhase), path);
      return () => clearPoll();
    }

    // Global tour not done — start from /inputs
    if (path === "/inputs") {
      let idx = 0;
      while (idx < PHASES.length && isDone(PHASES[idx].id)) idx++;
      if (idx >= PHASES.length) { markAllDone(); return; }
      if (PHASES[idx].path === "/inputs") {
        startPhase(idx, path);
      }
    }

    return () => clearPoll();
  }, [location.pathname, startPhase, clearPoll]);

  // Joyride callback
  const handleCallback = useCallback((data) => {
    const { action, index, type, status } = data;

    if (status === STATUS.SKIPPED) {
      markAllDone();
      setRun(false);
      runningRef.current = false;
      return;
    }

    if (status === STATUS.FINISHED) {
      const phase = PHASES[phaseRef.current];
      markDone(phase.id);
      setRun(false);
      runningRef.current = false;

      if (!isDone("global")) {
        const next = phaseRef.current + 1;
        if (next < PHASES.length) {
          setTimeout(() => startPhase(next, location.pathname), 400);
        } else {
          markAllDone();
        }
      }
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) setStepIndex(index + 1);
      else if (action === ACTIONS.PREV) setStepIndex(index - 1);
    }

    // Target not found mid-tour — auto-advance
    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (index < steps.length - 1) setStepIndex(index + 1);
      else {
        // Last step target not found — finish this phase
        const phase = PHASES[phaseRef.current];
        markDone(phase.id);
        setRun(false);
        runningRef.current = false;
        if (!isDone("global")) {
          const next = phaseRef.current + 1;
          if (next < PHASES.length) {
            setTimeout(() => startPhase(next, location.pathname), 400);
          } else {
            markAllDone();
          }
        }
      }
    }
  }, [steps.length, startPhase, location.pathname]);

  const isLastPhase = isDone("global") || phaseRef.current >= PHASES.length - 1;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      showSkipButton
      hideCloseButton
      disableOverlayClose
      spotlightClicks={false}
      callback={handleCallback}
      tooltipComponent={(props) => <CustomTooltip {...props} isLastPhase={isLastPhase} />}
      styles={{
        options: { zIndex: 10000, arrowColor: "#1a1a2e" },
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
      }}
      floaterProps={{ disableAnimation: true }}
    />
  );
}
